import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { useDroppable, useDraggable, useDndMonitor } from '@dnd-kit/core';
import {
  ArrowsOutCardinalIcon,
  GearIcon,
  PlusIcon,
  PuzzlePieceIcon,
  SquaresFourIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { REPORT_CANVAS_COLUMNS } from '@erp/shared-types';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  CanvasItemDragData,
  GridDropData,
  GridLayout,
  LayoutItem,
  PaletteItemDragData,
  ReportComponentConfig,
} from './types';
import {
  useComponentDefinition,
  useComponentRegistry,
} from './registry-context';
import {
  columnPitch,
  GRID_ROW_HEIGHT,
  GRID_ROW_PITCH,
  GRID_STYLE,
  gridItemStyle,
  gridRowsHeight,
} from './grid';
import { nudgeItem } from './layout';
import {
  canvasRows,
  configureAffordance,
  EMPTY_GRID_ROWS,
  landingKey,
  resizedLayout,
  shownLayout,
} from './canvasView';
import { subscribeChartWarnings } from './chart-diagnostics';
import type { ComponentTypeDefinition } from './registry';
import { useComponentDataset } from './ReportDataContext';
import { ComponentFrame, useFramed } from './ComponentFrame';
import { SourceSettingsForm } from './SourceSettingsForm';
import { useCanvasRecovery } from './canvasRecovery';
import {
  ChartErrorBoundary,
  ChartIssueIcon,
  ChartFitContext,
  chartFitFor,
  ConfigureAffordanceContext,
} from './charts/shared';
import { useElementSize } from './charts/useElementSize';
import {
  GRID_DROP_ID,
  landDrag,
  type LayoutUpdate,
} from './useReportCanvasDnd';
import { ReportDragPreview } from './ReportDragPreview';

// ── Component renderer ────────────────────────────────────────────────────────

function UnavailableComponent({ type }: { type: string }) {
  const recovery = useCanvasRecovery();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4">
      <PuzzlePieceIcon className="h-8 w-8 text-muted-foreground/40" />
      <p className="text-center text-xs text-muted-foreground">
        <T>This component is not available here.</T>
      </p>
      <p className="text-center text-[10px] text-muted-foreground/60">{type}</p>
      {recovery && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={recovery.onReset}
        >
          {recovery.label}
        </Button>
      )}
    </div>
  );
}

/**
 * A component's title: the one its config sets, else its type's default
 * (`defaultTitle`, which `fallback` is). Null for a component that shows none.
 */
export function useComponentTitle(component: ReportComponentConfig): {
  title: string | null;
  fallback: string | null;
} {
  const gt = useGT();
  const definition = useComponentDefinition(component.type);
  const dataset = useComponentDataset(component);
  const fallback =
    definition?.defaultTitle?.(component, { dataset, gt }) ?? null;
  const own = component.title?.trim();
  return { title: own || fallback, fallback };
}

export function ComponentRenderer({
  component,
}: {
  component: ReportComponentConfig;
}) {
  const definition = useComponentDefinition(component.type);
  const { title } = useComponentTitle(component);
  const framed = useFramed(component);
  const resetKey = JSON.stringify(component);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [capturedKey, setCapturedKey] = useState(resetKey);
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(containerRef);
  const fit = useMemo(() => chartFitFor(size), [size]);

  // Clear warnings when component config changes
  if (capturedKey !== resetKey) {
    setCapturedKey(resetKey);
    setWarnings([]);
  }

  useEffect(() => {
    return subscribeChartWarnings((msg) => {
      setWarnings((prev) => (prev.includes(msg) ? prev : [...prev, msg]));
    });
  }, []);

  if (!definition) {
    return <UnavailableComponent type={component.type} />;
  }
  const Renderer = definition.Renderer;
  const body = (
    <div ref={containerRef} className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col">
        <ChartErrorBoundary resetKey={resetKey}>
          <ChartFitContext.Provider value={fit}>
            <Renderer component={component} />
          </ChartFitContext.Provider>
        </ChartErrorBoundary>
      </div>
      {warnings.length > 0 && (
        <div className="absolute bottom-1.5 right-1.5 z-10">
          <ChartIssueIcon messages={warnings} />
        </div>
      )}
    </div>
  );

  if (framed)
    return (
      <ComponentFrame
        component={component}
        title={title}
        rendersOwnTitle={definition.rendersOwnTitle === true}
        body={body}
      />
    );

  return (
    <>
      {title && !definition.rendersOwnTitle && (
        <p
          className="truncate px-2.5 pt-2 text-xs font-medium text-foreground"
          title={title}
          data-component-title
        >
          {title}
        </p>
      )}
      <div ref={containerRef} className="relative flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col">
          <ChartErrorBoundary resetKey={resetKey}>
            <ChartFitContext.Provider value={fit}>
              <Renderer component={component} />
            </ChartFitContext.Provider>
          </ChartErrorBoundary>
        </div>
        {warnings.length > 0 && (
          <div className="absolute bottom-1.5 right-1.5 z-10">
            <ChartIssueIcon messages={warnings} />
          </div>
        )}
      </div>
    </>
  );
}

// ── Canvas item ───────────────────────────────────────────────────────────────

/** The id of the component a palette drag would add, while it is previewed. */
const DROP_PREVIEW_ID = '__drop__';

type ResizeEdge = 'right' | 'bottom' | 'corner';
const RESIZE_EDGES: readonly ResizeEdge[] = ['right', 'bottom', 'corner'];

/**
 * A handle on an item's right edge, bottom edge or bottom-right corner: drag
 * it to span more or fewer columns and rows, snapping to the grid (or press
 * the arrows its edge moves along). A button, so the canvas drag sensor never
 * starts a move from it. `onResize` previews each size; `onCommit` keeps the
 * last one when the pointer lets go.
 */
function ResizeHandle({
  edge,
  size,
  onResize,
  onCommit,
}: {
  edge: ResizeEdge;
  size: { w: number; h: number };
  onResize: (size: { w: number; h: number }) => void;
  onCommit: (size: { w: number; h: number }) => void;
}) {
  const gt = useGT();
  const widens = edge !== 'bottom';
  const heightens = edge !== 'right';
  const label = {
    right: gt('Resize width: {w} of {columns} columns', {
      w: size.w,
      columns: REPORT_CANVAS_COLUMNS,
      context: 'report canvas resize handle on the right edge',
    }),
    bottom: gt('Resize height: {h} rows', {
      h: size.h,
      context: 'report canvas resize handle on the bottom edge',
    }),
    corner: gt('Resize: {w} columns by {h} rows', {
      w: size.w,
      h: size.h,
      context: 'report canvas resize handle on the bottom-right corner',
    }),
  }[edge];
  const place = {
    right: 'inset-y-2 right-0 w-2 cursor-ew-resize',
    bottom: 'inset-x-2 bottom-0 h-2 cursor-ns-resize',
    corner: 'bottom-0 right-0 h-3 w-3 cursor-nwse-resize',
  }[edge];
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        'absolute z-30 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
        place,
      )}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        const step = {
          ArrowLeft: widens ? { w: -1, h: 0 } : null,
          ArrowRight: widens ? { w: 1, h: 0 } : null,
          ArrowUp: heightens ? { w: 0, h: -1 } : null,
          ArrowDown: heightens ? { w: 0, h: 1 } : null,
        }[e.key];
        if (!step) return;
        e.preventDefault();
        e.stopPropagation();
        onCommit({ w: size.w + step.w, h: size.h + step.h });
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        const grid = e.currentTarget.closest('[data-canvas-grid]');
        if (!(grid instanceof HTMLElement)) return;
        const pitch = columnPitch(grid.getBoundingClientRect().width);
        const start = { x: e.clientX, y: e.clientY };
        let last = size;
        const sizeAt = (event: PointerEvent) => ({
          w:
            size.w +
            (widens ? Math.round((event.clientX - start.x) / pitch) : 0),
          h:
            size.h +
            (heightens
              ? Math.round((event.clientY - start.y) / GRID_ROW_PITCH)
              : 0),
        });
        const move = (event: PointerEvent) => {
          const next = sizeAt(event);
          if (next.w !== last.w || next.h !== last.h) {
            last = next;
            onResize(next);
          }
        };
        const up = (event: PointerEvent) => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          onCommit(sizeAt(event));
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      }}
    >
      <span
        className={cn(
          'absolute rounded-full bg-primary/60',
          edge === 'right' && 'inset-y-0 right-0.5 m-auto h-8 w-1',
          edge === 'bottom' && 'inset-x-0 bottom-0.5 m-auto h-1 w-8',
          edge === 'corner' && 'bottom-0.5 right-0.5 h-2 w-2',
        )}
      />
    </button>
  );
}

/**
 * The toolbar's move handle: the keyboard's way to lay the grid out. Arrows
 * move the item a column or past the next item up or down; Shift+arrows
 * resize it.
 */
function MoveHandle({
  item,
  onNudge,
  onResize,
}: {
  item: LayoutItem;
  onNudge: (direction: 'left' | 'right' | 'up' | 'down') => void;
  onResize: (size: { w: number; h: number }) => void;
}) {
  const gt = useGT();
  return (
    <button
      type="button"
      className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
      aria-label={gt(
        'Move component: column {x}, row {y}, {w} by {h}. Arrow keys move it; Shift and the arrow keys resize it.',
        {
          x: item.x + 1,
          y: item.y + 1,
          w: item.w,
          h: item.h,
          context: 'report canvas move handle',
        },
      )}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        const direction = (
          {
            ArrowLeft: 'left',
            ArrowRight: 'right',
            ArrowUp: 'up',
            ArrowDown: 'down',
          } as const
        )[e.key];
        if (!direction) return;
        e.preventDefault();
        e.stopPropagation();
        if (!e.shiftKey) return onNudge(direction);
        onResize({
          w:
            item.w +
            (direction === 'right' ? 1 : direction === 'left' ? -1 : 0),
          h: item.h + (direction === 'down' ? 1 : direction === 'up' ? -1 : 0),
        });
      }}
    >
      <ArrowsOutCardinalIcon className="h-4 w-4" />
    </button>
  );
}

interface CanvasItemProps {
  item: LayoutItem;
  isAnyDragging: boolean;
  /** The item being dragged: shown faint where it would land. */
  isLanding: boolean;
  onDelete: () => void;
  onUpdate: (component: ReportComponentConfig) => void;
  /** Absent = the item has no move or resize handles. */
  arrange?: {
    onNudge: (direction: 'left' | 'right' | 'up' | 'down') => void;
    onResizePreview: (size: { w: number; h: number } | null) => void;
    onResize: (size: { w: number; h: number }) => void;
  };
  /** Set when the host edits the selected component elsewhere (a sidebar). */
  selection?: { isSelected: boolean; onSelect: () => void };
}

/** A canvas item's hover toolbar: move, configure and delete. */
function CanvasItemToolbar({
  item,
  isAnyDragging,
  configurable,
  onDelete,
  arrange,
}: Pick<CanvasItemProps, 'item' | 'isAnyDragging' | 'onDelete' | 'arrange'> & {
  configurable: boolean;
}) {
  const gt = useGT();
  return (
    <div
      className={cn(
        'absolute right-1 top-1 z-20 flex items-center gap-0.5 rounded bg-card/90 px-1 py-0.5 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 focus-within:opacity-100',
        isAnyDragging && 'opacity-0',
      )}
    >
      {arrange && (
        <MoveHandle
          item={item}
          onNudge={arrange.onNudge}
          onResize={arrange.onResize}
        />
      )}
      {configurable && (
        <PopoverTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={gt('Configure component')}
          >
            <GearIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="rounded p-0.5 text-muted-foreground hover:text-destructive focus-visible:outline-2 focus-visible:outline-ring"
        aria-label={gt('Delete component')}
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

/** A canvas item's resize handles on each edge, previewing as they drag. */
function CanvasItemResizeHandles({
  item,
  arrange,
}: {
  item: LayoutItem;
  arrange: NonNullable<CanvasItemProps['arrange']>;
}) {
  const size = { w: item.w, h: item.h };
  return RESIZE_EDGES.map((edge) => (
    <ResizeHandle
      key={edge}
      edge={edge}
      size={size}
      onResize={arrange.onResizePreview}
      onCommit={(next) => {
        arrange.onResizePreview(null);
        arrange.onResize(next);
      }}
    />
  ));
}

/**
 * The config form a canvas item's popover shows; none with a selection, as
 * the host's sidebar edits the component then, or for a type without one.
 */
function popoverConfigForm(
  definition: ComponentTypeDefinition | null,
  hasSelection: boolean,
  component: ReportComponentConfig,
): ComponentTypeDefinition['ConfigForm'] | null {
  if (hasSelection) return null;
  // A component with its own source is set up by its source, not by picking
  // a dataset's columns: its settings are its title, range pin and JSON.
  if (definition && component.source) return SourceSettingsForm;
  return definition?.ConfigForm ?? null;
}

/** How a canvas item takes part in the host's selection, if it has one. */
function cellSelection(selection: CanvasItemProps['selection']) {
  return {
    onClick: selection?.onSelect,
    ariaSelected: selection?.isSelected,
    isSelected: selection?.isSelected === true,
  };
}

/** A canvas item's configure popover: its type's name over its config form. */
function CanvasItemConfig({
  label,
  ConfigForm,
  component,
  onUpdate,
  onClose,
}: {
  label: string;
  ConfigForm: NonNullable<ComponentTypeDefinition['ConfigForm']>;
  component: ReportComponentConfig;
  onUpdate: (component: ReportComponentConfig) => void;
  onClose: () => void;
}) {
  return (
    <PopoverContent className="w-64 p-0" align="end" side="bottom">
      <div className="border-b px-3 py-2.5">
        <p className="text-sm font-medium">{label}</p>
      </div>
      <div className="max-h-[60vh] overflow-y-auto p-3">
        <ConfigForm
          component={component}
          onUpdate={onUpdate}
          onClose={onClose}
        />
      </div>
    </PopoverContent>
  );
}

function CanvasItem({
  item,
  isAnyDragging,
  isLanding,
  onDelete,
  onUpdate,
  arrange,
  selection,
}: CanvasItemProps) {
  const definition = useComponentDefinition(item.component.type);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `canvas:${item.id}`,
    data: {
      kind: 'canvas',
      itemId: item.id,
      componentType: item.component.type,
    } satisfies CanvasItemDragData,
  });

  const ConfigForm = popoverConfigForm(
    definition,
    selection !== undefined,
    item.component,
  );
  const cell = cellSelection(selection);

  return (
    <Popover open={isConfigOpen} onOpenChange={setIsConfigOpen}>
      <div
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        style={gridItemStyle(item)}
        onClick={cell.onClick}
        aria-selected={cell.ariaSelected}
        data-canvas-cell={item.id}
        className={cn(
          'group relative z-10 flex cursor-grab flex-col overflow-hidden rounded-md border border-border bg-card transition-opacity active:cursor-grabbing',
          isLanding && 'border-primary/60 opacity-40',
          cell.isSelected && 'ring-2 ring-primary',
        )}
      >
        <CanvasItemToolbar
          item={item}
          isAnyDragging={isAnyDragging}
          configurable={ConfigForm !== null}
          onDelete={onDelete}
          arrange={arrange}
        />
        <ConfigureAffordanceContext.Provider
          value={configureAffordance(Boolean(selection), ConfigForm !== null)}
        >
          <ComponentRenderer component={item.component} />
        </ConfigureAffordanceContext.Provider>
        {arrange && !isAnyDragging && (
          <CanvasItemResizeHandles item={item} arrange={arrange} />
        )}
      </div>

      {ConfigForm !== null && (
        <CanvasItemConfig
          label={definition?.label ?? item.component.type}
          ConfigForm={ConfigForm}
          component={item.component}
          onUpdate={onUpdate}
          onClose={() => setIsConfigOpen(false)}
        />
      )}
    </Popover>
  );
}

// ── Main canvas ───────────────────────────────────────────────────────────────

/**
 * The grid's cells, faint behind the items while editing, so places and sizes
 * read as cells: one stripe of row cells per column.
 */
function GridBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{ ...GRID_STYLE, gridAutoRows: undefined }}
    >
      {Array.from({ length: REPORT_CANVAS_COLUMNS }, (_, column) => (
        <div
          key={column}
          className="rounded-sm"
          style={{
            backgroundImage: `linear-gradient(to bottom, color-mix(in oklab, var(--color-muted) 70%, transparent) ${GRID_ROW_HEIGHT}px, transparent ${GRID_ROW_HEIGHT}px)`,
            backgroundSize: `100% ${GRID_ROW_PITCH}px`,
          }}
        />
      ))}
    </div>
  );
}

/** Adds a component at the bottom of the grid. */
function AddComponent({ onAdd }: { onAdd: (type: string) => void }) {
  const registry = useComponentRegistry();
  return (
    <div className="flex h-7 shrink-0 items-center justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
          >
            <PlusIcon className="h-4 w-4" />
            <T context="report canvas add a component at the bottom">Add</T>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {Object.values(registry).map((definition) => (
            <DropdownMenuItem
              key={definition.type}
              onSelect={() => onAdd(definition.type)}
            >
              {definition.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** In place of the items on an empty canvas: where to start. */
function EmptyCanvasHint({ isDragging }: { isDragging: boolean }) {
  return (
    <div
      className="pointer-events-none z-10 flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-muted/30 text-center"
      style={{
        gridColumn: '1 / -1',
        gridRow: `1 / span ${EMPTY_GRID_ROWS}`,
      }}
    >
      <SquaresFourIcon className="h-10 w-10 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">
        {isDragging ? (
          <T>Drop here to add to canvas</T>
        ) : (
          <T>Drag components from the Report builder panel to get started</T>
        )}
      </p>
    </div>
  );
}

/** An item's move and resize handlers, committing through `onArrange`. */
function itemArrange(
  id: string,
  onArrange: LayoutUpdate,
  setResizing: (resizing: { id: string; w: number; h: number } | null) => void,
  resize: (prev: GridLayout, size: { w: number; h: number }) => GridLayout,
): NonNullable<CanvasItemProps['arrange']> {
  return {
    onNudge: (direction) =>
      onArrange((prev) => nudgeItem(prev, id, direction), id),
    onResizePreview: (size) => setResizing(size && { id, ...size }),
    onResize: (size) => onArrange((prev) => resize(prev, size), id),
  };
}

/** An item's part in the host's selection. */
function itemSelection(
  id: string,
  selection: NonNullable<ReportCanvasProps['selection']>,
): NonNullable<CanvasItemProps['selection']> {
  return {
    isSelected: selection.itemId === id,
    onSelect: () => selection.onSelect(id),
  };
}

interface ReportCanvasProps {
  layout: GridLayout;
  isAnyDragging: boolean;
  onDelete: (itemId: string) => void;
  onUpdate: (itemId: string, component: ReportComponentConfig) => void;
  /**
   * Moves and resizes from the canvas's own handles and keys (drags land
   * through the drag-and-drop controller); the items have no handles without it.
   */
  onArrange?: LayoutUpdate;
  /** Adds a component of `type` at the bottom; when set, an Add control follows the grid. */
  onAdd?: (type: string) => void;
  /**
   * Set when the host edits the selected component in its own panel: clicking
   * an item selects it, and items have no configure popover.
   */
  selection?: {
    itemId: string | null;
    onSelect: (itemId: string) => void;
  };
}

/**
 * The editing canvas: the layout's items on the Report grid, with the grid's
 * cells behind them. Dragging an item (or a palette tile) over the grid
 * previews where it lands, the others reflowing around it; the drop itself
 * is committed by the drag-and-drop controller (`useReportCanvasDnd`) with
 * the same `landDrag`, so it lands where the preview showed.
 */
export function ReportCanvas({
  layout,
  isAnyDragging,
  onDelete,
  onUpdate,
  onArrange,
  onAdd,
  selection,
}: ReportCanvasProps) {
  const registry = useComponentRegistry();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const { setNodeRef, isOver } = useDroppable({
    id: GRID_DROP_ID,
    data: {
      kind: 'grid',
      measure: () => {
        const rect = gridRef.current?.getBoundingClientRect();
        return rect
          ? { left: rect.left, top: rect.top, width: rect.width }
          : null;
      },
    } satisfies GridDropData,
  });
  // The layout a drag over the grid would leave, and the item landing; only
  // re-set when the landing cell changes, not on every pointer move.
  const [dragPreview, setDragPreview] = useState<{
    layout: GridLayout;
    id: string;
    key: string;
  } | null>(null);
  const [resizing, setResizing] = useState<{
    id: string;
    w: number;
    h: number;
  } | null>(null);

  useDndMonitor({
    onDragMove(event) {
      const landed = landDrag(layout, registry, event, DROP_PREVIEW_ID);
      const key = landingKey(landed);
      if (key === (dragPreview?.key ?? '')) return;
      setDragPreview(landed && { ...landed, key });
    },
    onDragEnd() {
      setDragPreview(null);
    },
    onDragCancel() {
      setDragPreview(null);
    },
  });

  const preview = isAnyDragging ? dragPreview : null;
  const shown = shownLayout(
    registry,
    layout,
    preview?.layout ?? null,
    resizing,
  );
  const editing = onArrange !== undefined;
  const rows = canvasRows(shown, editing);

  return (
    <div className="w-full p-2">
      <div
        ref={(node) => {
          gridRef.current = node;
          setNodeRef(node);
        }}
        data-canvas-grid
        className={cn(
          'relative rounded-md',
          isAnyDragging &&
            isOver &&
            'outline-2 outline-offset-4 outline-primary/30',
        )}
        style={{ ...GRID_STYLE, height: gridRowsHeight(rows) }}
      >
        {editing && <GridBackdrop />}
        {shown.length === 0 && <EmptyCanvasHint isDragging={isAnyDragging} />}
        {shown.map((item) =>
          item.id === DROP_PREVIEW_ID ? (
            <div
              key={item.id}
              aria-hidden
              style={gridItemStyle(item)}
              className="z-10 rounded-md border border-primary/50 bg-primary/10"
            />
          ) : (
            <CanvasItem
              key={item.id}
              item={item}
              isAnyDragging={isAnyDragging}
              isLanding={preview?.id === item.id}
              onDelete={() => onDelete(item.id)}
              onUpdate={(component) => onUpdate(item.id, component)}
              arrange={
                onArrange &&
                itemArrange(item.id, onArrange, setResizing, (prev, size) =>
                  resizedLayout(registry, prev, item.id, size),
                )
              }
              selection={selection && itemSelection(item.id, selection)}
            />
          ),
        )}
      </div>
      {onAdd && !isAnyDragging && (
        <div className="pt-1">
          <AddComponent onAdd={onAdd} />
        </div>
      )}
    </div>
  );
}

// ── Palette draggable item ────────────────────────────────────────────────────

interface PaletteItemProps {
  componentType: string;
  label: string;
  icon?: React.ReactNode;
  preview?: React.ReactNode;
  /** A click (not a drag) on the tile. */
  onClick?: () => void;
}

export function PaletteItem({
  componentType,
  label,
  icon,
  preview,
  onClick,
}: PaletteItemProps) {
  const uid = useId();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${componentType}:${uid}`,
    data: { kind: 'palette', componentType } satisfies PaletteItemDragData,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cn(
        'flex cursor-grab flex-col items-center gap-2 rounded-md border border-border bg-card p-3 text-center select-none transition-opacity active:cursor-grabbing',
        onClick && 'hover:border-primary/40',
        isDragging && 'opacity-40',
      )}
    >
      {preview ? <div className="w-full">{preview}</div> : icon}
      <span className="text-sm leading-tight">{label}</span>
    </div>
  );
}

// ── Drag overlay preview ──────────────────────────────────────────────────────

/** Drag preview resolved from the active registry (for overlay hosts). */
export function ReportDragPreviewForType({ type }: { type: string }) {
  const definition = useComponentDefinition(type);
  if (!definition) return null;
  return <ReportDragPreview definition={definition} />;
}
